import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { THEME_COLOR, buttonStyles } from '../../../theme/formStyles';
import { engineerName, fmtDate, fmtDuration, isOnDay, isOpenVisit, todayYmd, visitMinutes } from '../projectSchedule.utils';
import { TimesGrid } from '../schedule/VisitPanel';

function VisitRow({ task, line, onOpen }) {
    return (
        <Box onClick={() => onOpen(task)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '11px 12px', border: '1px solid #e5e9ee', borderRadius: 3, mb: 1, cursor: 'pointer', bgcolor: '#fff', '&:hover': { borderColor: THEME_COLOR } }}>
            <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 1, bgcolor: THEME_COLOR }} />
            <Box sx={{ width: 64, flex: 'none' }}>
                <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>{fmtDate(task.start_date, 'D MMM')}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>{fmtDate(task.start_date, 'ddd')}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500 }} noWrap>{task.sub_task_name}</Typography>
                <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }} noWrap>{line}</Typography>
            </Box>
        </Box>
    );
}

// The client's landing view: today's visit (or the next one), the next few visits, and the latest report
export default function ClientTodayPanel({ tasks, visits, onOpen, onShowCalendar }) {
    const today = todayYmd();
    const todays = tasks.filter(t => isOnDay(t, today));
    const upcoming = tasks.filter(t => t.start_date > today).sort((a, b) => a.start_date.localeCompare(b.start_date));
    const todayVisits = visits.filter(v => v.visit_date === today && todays.some(t => t.id === v.sub_task_id));
    const live = todayVisits.some(isOpenVisit);
    const latestDone = visits.filter(v => v.check_out_at).sort((a, b) => b.visit_date.localeCompare(a.visit_date))[0];
    const latestTask = latestDone && tasks.find(t => t.id === latestDone.sub_task_id);

    let hero;
    if (todays.length) {
        const first = todays[0];
        const visit = todayVisits[0];
        hero = (
            <>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: THEME_COLOR, textTransform: 'uppercase', letterSpacing: '.05em', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: live ? '#16a34a' : '#9fd8c8' }} />
                    {live ? 'Engineer on site now' : visit?.check_out_at ? 'Visit finished today' : "Visit today · engineer hasn't arrived yet"} · {fmtDate(today)}
                </Typography>
                <Typography sx={{ fontSize: { xs: '18px', md: '20px' }, fontWeight: 500, my: 1.25 }}>{todays.map(t => t.sub_task_name).join(' + ')}</Typography>
                <Typography sx={{ fontSize: '13.5px', mb: 1.25 }}><b style={{ fontWeight: 500 }}>{first.spoc ? engineerName(first) : 'Engineer to be assigned'}</b> · Field engineer</Typography>
                <TimesGrid visit={visit} />
                <Button variant="contained" sx={buttonStyles.contained} onClick={() => onOpen(first)}>View visit details</Button>
            </>
        );
    } else if (upcoming[0]) {
        const next = upcoming[0];
        hero = (
            <>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: THEME_COLOR, textTransform: 'uppercase', letterSpacing: '.05em' }}>Next visit · {fmtDate(next.start_date, 'dddd D MMM')}</Typography>
                <Typography sx={{ fontSize: { xs: '18px', md: '20px' }, fontWeight: 500, my: 1.25 }}>{next.sub_task_name}</Typography>
                <Typography sx={{ fontSize: '13.5px', mb: 1.5 }}>{next.spoc ? engineerName(next) : 'Engineer to be assigned'}</Typography>
                <Button variant="contained" sx={buttonStyles.contained} onClick={() => onOpen(next)}>View details</Button>
            </>
        );
    } else {
        hero = <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>No visits scheduled</Typography>;
    }

    return (
        <Box>
            <Box sx={{ borderRadius: 4, p: 2.25, background: 'linear-gradient(135deg,#e2f5ef,#f3fbf8)', border: '1px solid #cfeae2' }}>{hero}</Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 2.25, mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>Coming up</Typography>
                <Button size="small" sx={{ color: THEME_COLOR, textTransform: 'none' }} onClick={onShowCalendar}>Full calendar →</Button>
            </Box>
            {upcoming.slice(0, 3).map(t => <VisitRow key={t.id} task={t} onOpen={onOpen} line={t.spoc ? engineerName(t) : 'Engineer to be assigned'} />)}
            {!upcoming.length && <Typography sx={{ textAlign: 'center', color: '#6b7785', fontSize: '13px', py: 1.5 }}>Nothing else scheduled.</Typography>}

            {latestDone && latestTask && (
                <>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', mt: 2.25, mb: 1 }}>Latest visit report</Typography>
                    <VisitRow task={{ ...latestTask, start_date: latestDone.visit_date }} onOpen={() => onOpen(latestTask)}
                        line={`${fmtDate(latestDone.visit_date)} · ${fmtDuration(visitMinutes(latestDone))} on site · open to download`} />
                </>
            )}
        </Box>
    );
}
